import React from 'react'
import { Check, ArrowRight } from 'lucide-react'
import Container from './Container'
import AnimatedSection from './AnimatedSection'

/**
 * BenefitsSection - CONFOTUR and fiscal benefits section
 */

const BENEFITS = [
  'Posibles exenciones fiscales bajo regímenes aplicables (ej. CONFOTUR)',
  'Mejor proyección de rentabilidad neta (según condiciones del proyecto)',
  'Mayor atractivo para renta vacacional en zonas turísticas',
  'Inversión con enfoque a valorización en ubicaciones clave',
]

const BenefitsSection: React.FC = () => {
  return (
    <section id="beneficios" className="bg-background py-24 md:py-32">
      <Container size="xl">
        <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-2 lg:gap-16">
          {/* Content Side */}
          <AnimatedSection>
            {/* Section Label */}
            <span className="section-label mb-4 block">Ventajas</span>
            <h2 className="accent-line mb-6 text-3xl font-bold text-primary md:text-4xl lg:text-5xl">
              Beneficios que pueden mejorar tu retorno
            </h2>
            <p className="mb-8 text-lg text-text-secondary">
              Dependiendo del proyecto y su certificación, podrías acceder a incentivos que hacen
              más eficiente tu inversión.
            </p>

            {/* Benefits List */}
            <ul className="mb-8 space-y-4">
              {BENEFITS.map((benefit, index) => (
                <li key={index} className="flex items-start">
                  <Check
                    className="mr-3 mt-0.5 h-6 w-6 flex-shrink-0 text-accent"
                    aria-hidden={true}
                  />
                  <span className="text-text-secondary">{benefit}</span>
                </li>
              ))}
            </ul>

            {/* Legal Note */}
            <p className="mb-8 text-sm italic text-text-muted">
              Los beneficios pueden variar por proyecto. Se confirman en asesoría según
              documentación y estatus vigente.
            </p>

            {/* CTA */}
            <a
              href="#contacto"
              className="group inline-flex items-center justify-center rounded-lg bg-accent px-8 py-4 font-semibold text-white transition-all duration-300 hover:bg-accent-dark focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2"
            >
              Agendar llamada y validar beneficios
              <ArrowRight className="btn-arrow ml-2 h-5 w-5" aria-hidden={true} />
            </a>
          </AnimatedSection>

          {/* Visual Side - Decorative Element */}
          <AnimatedSection delay={0.2}>
            <div className="rounded-2xl bg-gradient-to-br from-accent/20 to-accent-light/10 p-8 md:p-12">
              {/* Decorative Cards */}
              <div className="space-y-6">
                <div className="shadow-luxury card-hover rounded-xl bg-white p-6">
                  <div className="mb-4 flex items-center">
                    <div className="mr-4 flex h-12 w-12 items-center justify-center rounded-lg bg-accent/10">
                      <span className="text-2xl font-bold text-accent">%</span>
                    </div>
                    <div>
                      <h4 className="font-semibold text-primary">Exenciones fiscales</h4>
                      <p className="text-sm text-text-muted">CONFOTUR aplicable</p>
                    </div>
                  </div>
                  <div className="h-2 overflow-hidden rounded-full bg-secondary-light">
                    <div className="h-full w-3/4 rounded-full bg-accent" />
                  </div>
                </div>

                <div className="shadow-luxury card-hover rounded-xl bg-white p-6">
                  <div className="mb-4 flex items-center">
                    <div className="mr-4 flex h-12 w-12 items-center justify-center rounded-lg bg-accent/10">
                      <span className="text-2xl font-bold text-accent">↗</span>
                    </div>
                    <div>
                      <h4 className="font-semibold text-primary">Plusvalía</h4>
                      <p className="text-sm text-text-muted">Potencial de crecimiento</p>
                    </div>
                  </div>
                  <div className="h-2 overflow-hidden rounded-full bg-secondary-light">
                    <div className="h-full w-4/5 rounded-full bg-accent" />
                  </div>
                </div>

                <div className="shadow-luxury card-hover rounded-xl bg-white p-6">
                  <div className="mb-4 flex items-center">
                    <div className="mr-4 flex h-12 w-12 items-center justify-center rounded-lg bg-accent/10">
                      <span className="text-2xl font-bold text-accent">🏖</span>
                    </div>
                    <div>
                      <h4 className="font-semibold text-primary">Renta vacacional</h4>
                      <p className="text-sm text-text-muted">Alto retorno turístico</p>
                    </div>
                  </div>
                  <div className="h-2 overflow-hidden rounded-full bg-secondary-light">
                    <div className="h-full w-2/3 rounded-full bg-accent" />
                  </div>
                </div>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </Container>
    </section>
  )
}

export default BenefitsSection
